import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowUpRight, RefreshCw } from "lucide-react";

const INTEREST_OPTIONS = [
  "Building",
  "Robotics",
  "Coding",
  "STEM",
  "Brick-based",
  "Sports",
  "Camps",
];

const GOAL_OPTIONS = [
  "Confidence",
  "Social Skills",
  "Engineering",
  "Coding",
  "Problem Solving",
  "Physical Activity",
];

const AGE_BUCKETS = [
  { label: "4–6", min: 4, max: 6 },
  { label: "7–9", min: 7, max: 9 },
  { label: "10–12", min: 10, max: 12 },
  { label: "13–17", min: 13, max: 17 },
];

function score(program, { ageMin, ageMax, interests, goals }) {
  let s = 0;
  const pAgeMin = program.age_min ?? 4;
  const pAgeMax = program.age_max ?? 17;
  // Age overlap is required — programs that don't fit the age get a heavy penalty.
  const ageOverlap = ageMin <= pAgeMax && ageMax >= pAgeMin;
  if (!ageOverlap) return 0;
  s += 5;
  const pInterests = (program.interests || []).map((x) => x.toLowerCase());
  interests.forEach((i) => {
    if (pInterests.includes(i.toLowerCase())) s += 4;
  });
  const pGoals = (program.goals || []).map((x) => x.toLowerCase());
  goals.forEach((g) => {
    if (pGoals.includes(g.toLowerCase())) s += 3;
  });
  return s;
}

export default function ProgramRecommender({ programs = [] }) {
  const [age, setAge] = useState(null);
  const [interests, setInterests] = useState([]);
  const [goals, setGoals] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const toggle = (list, setList, val) =>
    setList(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const recommendations = useMemo(() => {
    if (!submitted || !age) return [];
    const ranked = programs
      .filter((p) => p.active !== false && p.visible !== false)
      .map((p) => ({ p, score: score(p, { ageMin: age.min, ageMax: age.max, interests, goals }) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((x) => x.p);
    return ranked;
  }, [submitted, age, interests, goals, programs]);

  const reset = () => {
    setAge(null);
    setInterests([]);
    setGoals([]);
    setSubmitted(false);
  };

  return (
    <div
      className="rounded-[2rem] bg-gradient-to-br from-[#FFFCF7] to-white border border-slate-200/70 p-7 sm:p-10 shadow-sm"
      data-testid="program-recommender"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFE6CC] text-[#9A3412]">
          <Sparkles className="h-5 w-5" />
        </span>
        <div>
          <p className="eyebrow">Recommendation Widget</p>
          <h3 className="font-display text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
            Find the right program for your child.
          </h3>
          <p className="mt-2 text-sm text-slate-600">
            Answer three quick questions and we'll suggest programs that fit.
          </p>
        </div>
      </div>

      {!submitted && (
        <div className="mt-8 grid gap-7">
          <div>
            <p className="eyebrow text-slate-600 mb-3">1. Child's age</p>
            <div className="flex flex-wrap gap-2">
              {AGE_BUCKETS.map((b) => (
                <button
                  key={b.label}
                  onClick={() => setAge(b)}
                  className={`pill border transition-colors ${
                    age?.label === b.label
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                  }`}
                  data-testid={`rec-age-${b.label.replace(/[^a-z0-9]+/gi, "-")}`}
                >
                  {b.label} yrs
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="eyebrow text-slate-600 mb-3">2. Interests (pick any)</p>
            <div className="flex flex-wrap gap-2">
              {INTEREST_OPTIONS.map((i) => (
                <button
                  key={i}
                  onClick={() => toggle(interests, setInterests, i)}
                  className={`pill border transition-colors ${
                    interests.includes(i)
                      ? "bg-sky-50 text-[#0369A1] border-[#0284C7]"
                      : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                  }`}
                  data-testid={`rec-interest-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="eyebrow text-slate-600 mb-3">3. Goals for your child</p>
            <div className="flex flex-wrap gap-2">
              {GOAL_OPTIONS.map((g) => (
                <button
                  key={g}
                  onClick={() => toggle(goals, setGoals, g)}
                  className={`pill border transition-colors ${
                    goals.includes(g)
                      ? "bg-amber-50 text-[#B45309] border-[#FF8C42]"
                      : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                  }`}
                  data-testid={`rec-goal-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <button
            disabled={!age}
            onClick={() => setSubmitted(true)}
            className="btn-primary mt-1 disabled:opacity-50 disabled:cursor-not-allowed self-start"
            data-testid="rec-submit"
          >
            Show my recommendations
            <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {submitted && (
        <div className="mt-8" data-testid="rec-results">
          {recommendations.length === 0 ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <p className="font-display font-bold text-slate-900">
                We couldn't find an exact match — but we still can help.
              </p>
              <p className="mt-2 text-sm text-slate-600">
                Tell us a bit more on the Trial Class page and we'll recommend the right fit.
              </p>
              <Link to="/trial-class" className="btn-primary mt-5">
                Book a trial conversation
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid gap-3">
              <p className="text-sm font-semibold text-slate-700">
                Top {recommendations.length} match{recommendations.length > 1 ? "es" : ""} for your child:
              </p>
              {recommendations.map((p) => (
                <Link
                  key={p.slug}
                  to={`/programs/${p.slug}`}
                  className="group flex items-center justify-between rounded-2xl bg-white border border-slate-200 p-5 hover:border-slate-300 transition-colors"
                  data-testid={`rec-result-${p.slug}`}
                >
                  <div className="min-w-0">
                    <p className="font-display font-bold text-slate-900 group-hover:text-[#0369A1]">
                      {p.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {p.age_range || p.ages} · {p.category}
                    </p>
                    <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                      {p.short_description || p.description}
                    </p>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-slate-400 shrink-0 ml-4 group-hover:text-[#0369A1]" />
                </Link>
              ))}
              <Link
                to="/trial-class"
                className="btn-primary mt-2 self-start"
                data-testid="rec-trial-cta"
              >
                Book a trial class
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          <button
            onClick={reset}
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-700"
            data-testid="rec-reset"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Start over
          </button>
        </div>
      )}
    </div>
  );
}

export { INTEREST_OPTIONS, GOAL_OPTIONS, AGE_BUCKETS };
